import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { Alert, Box, Button, CircularProgress, FormControl } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../../hooks';
import { usePlatformFunction } from '../../../../../hooks/usePlatformFunction';
import { SelectedRecipientsDialogContent } from './SelectedRecipientsDialogContent';
export const TotalRecipients = ({ recipients, channels, emailCc, disabled, requireRecipients, onCalculateRecipients, updateTotalRecipients }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    const { t } = useAlertUiTranslation();
    const contactMethods = [];
    if (channels && channels.includes('voice')) {
        contactMethods.push('voice');
    }
    if ((channels && channels.includes('sms')) || (channels && channels.includes('whatsapp'))) {
        contactMethods.push('text');
    }
    if (channels && channels.includes('fax')) {
        contactMethods.push('fax');
    }
    if (channels && channels.includes('email')) {
        contactMethods.push('email');
    }
    if (channels && channels.includes('pager')) {
        contactMethods.push('pager');
    }
    const sendingToInbox = channels ? channels.includes('inbox') : false;
    const sendingOnlyToInbox = contactMethods.length === 0 && sendingToInbox;
    const calculateResult = usePlatformFunction('calculateRecipients', {
        recipients: recipients,
        calculateAccountFilter: true,
        channels: channels,
    });
    const accountFilter = (_b = (_a = calculateResult.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.accountFilter;
    const accountWithoutContactDetailsFilter = accountFilter
        ? [
            ...accountFilter,
            {
                op: 'not',
                filter: [
                    {
                        column: 'contactMethodSet',
                        op: 'overlap',
                        value: contactMethods,
                    },
                ],
            },
        ]
        : undefined;
    const accountCount = useStorageList(accountEntityName, accountFilter
        ? {
            filter: accountFilter,
            limit: 0,
        }
        : undefined, undefined, undefined, {
        revalidateOnFocus: false,
    });
    const accountWithoutContactDetailsCount = useStorageList(accountEntityName, accountFilter && !sendingToInbox
        ? {
            filter: [
                ...accountFilter,
                {
                    op: 'not',
                    filter: [
                        {
                            column: 'contactMethodSet',
                            op: 'overlap',
                            value: contactMethods,
                        },
                    ],
                },
            ],
            limit: 0,
        }
        : undefined, undefined, undefined, {
        revalidateOnFocus: false,
    });
    const externalRecipientCount = usePlatformFunction('listExternalRecipients', ((_c = recipients.external) === null || _c === void 0 ? void 0 : _c.importId)
        ? {
            importId: recipients.external.importId,
            listRequest: {
                limit: 0,
            },
        }
        : undefined);
    const externalRecipientWithoutContactDetailsCount = usePlatformFunction('listExternalRecipients', ((_d = recipients.external) === null || _d === void 0 ? void 0 : _d.importId) && !sendingOnlyToInbox
        ? {
            importId: recipients.external.importId,
            listRequest: {
                filter: [
                    {
                        op: 'not',
                        filter: [
                            {
                                column: 'contactMethodSet',
                                op: 'overlap',
                                value: contactMethods,
                            },
                        ],
                    },
                ],
                limit: 0,
            },
        }
        : undefined);
    const isLoading = calculateResult.isLoading ||
        accountCount.isLoading ||
        accountWithoutContactDetailsCount.isLoading ||
        externalRecipientCount.isLoading ||
        externalRecipientWithoutContactDetailsCount.isLoading;
    const error = (_h = (_g = (_f = (_e = calculateResult.error) !== null && _e !== void 0 ? _e : accountCount.error) !== null && _f !== void 0 ? _f : accountWithoutContactDetailsCount.error) !== null && _g !== void 0 ? _g : externalRecipientCount.error) !== null && _h !== void 0 ? _h : externalRecipientWithoutContactDetailsCount.error;
    const accountTotal = (_k = (_j = accountCount.data) === null || _j === void 0 ? void 0 : _j.total) !== null && _k !== void 0 ? _k : 0;
    const accountWithoutContactDetailsTotal = sendingToInbox ? 0 : ((_m = (_l = accountWithoutContactDetailsCount.data) === null || _l === void 0 ? void 0 : _l.total) !== null && _m !== void 0 ? _m : 0);
    const externalRecipientTotal = (_q = (_p = (_o = externalRecipientCount.data) === null || _o === void 0 ? void 0 : _o.result) === null || _p === void 0 ? void 0 : _p.total) !== null && _q !== void 0 ? _q : 0;
    const externalRecipientWithoutContactDetailsTotal = sendingOnlyToInbox
        ? externalRecipientTotal
        : ((_t = (_s = (_r = externalRecipientWithoutContactDetailsCount.data) === null || _r === void 0 ? void 0 : _r.result) === null || _s === void 0 ? void 0 : _s.total) !== null && _t !== void 0 ? _t : 0);
    const emailCcTotal = (_u = emailCc === null || emailCc === void 0 ? void 0 : emailCc.split(',').length) !== null && _u !== void 0 ? _u : 0;
    const validRecipientsFound = accountTotal +
        externalRecipientTotal +
        emailCcTotal -
        accountWithoutContactDetailsTotal -
        externalRecipientWithoutContactDetailsTotal >
        0;
    React.useEffect(() => {
        onCalculateRecipients === null || onCalculateRecipients === void 0 ? void 0 : onCalculateRecipients(validRecipientsFound);
    }, [onCalculateRecipients, validRecipientsFound]);
    React.useEffect(() => {
        updateTotalRecipients(accountTotal + externalRecipientTotal + emailCcTotal);
    }, [updateTotalRecipients, accountTotal, externalRecipientTotal, emailCcTotal]);
    if (isLoading) {
        return (React.createElement(Box, { sx: {
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 2,
            } },
            React.createElement(CircularProgress, null)));
    }
    else if (error) {
        return React.createElement(Alert, { severity: 'error' }, getErrorMessage(error));
    }
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true },
        React.createElement(Button, { variant: 'outlined', sx: { p: 1.9, borderColor: themeColors.border.main }, disabled: disabled, onClick: () => {
                var _a;
                return showAlertDialog({
                    title: t('common.selectedRecipientsCount', { count: accountTotal + externalRecipientTotal + emailCcTotal }),
                    content: (React.createElement(SelectedRecipientsDialogContent, { accountFilter: accountFilter, importId: (_a = recipients.external) === null || _a === void 0 ? void 0 : _a.importId, externalRecipientFilter: [], emailCc: emailCc })),
                    closeLabel: t('common.cancel'),
                });
            } }, t('pages.edit.recipients.dialog.recipients', {
            count: accountTotal + externalRecipientTotal + emailCcTotal,
        })),
        requireRecipients && !validRecipientsFound && (accountTotal > 0 || externalRecipientTotal > 0) ? (React.createElement(Alert, { sx: { mt: 0.5 }, severity: 'error' }, t('pages.edit.recipients.dialog.noneOfTheRecipientsHaveContactDetails'))) : accountWithoutContactDetailsTotal + externalRecipientWithoutContactDetailsTotal > 0 ? (React.createElement(Alert, { sx: { mt: 0.5 }, severity: 'warning', action: React.createElement(Button, { color: 'inherit', size: 'small', onClick: () => {
                    var _a;
                    return showAlertDialog({
                        title: t('common.contactDetailsNotProvided', {
                            count: accountWithoutContactDetailsTotal + externalRecipientWithoutContactDetailsTotal,
                        }),
                        content: (React.createElement(SelectedRecipientsDialogContent, { accountFilter: accountWithoutContactDetailsTotal > 0 ? accountWithoutContactDetailsFilter : undefined, importId: (_a = recipients.external) === null || _a === void 0 ? void 0 : _a.importId, externalRecipientFilter: [
                                {
                                    op: 'not',
                                    filter: [
                                        {
                                            column: 'contactMethodSet',
                                            op: 'overlap',
                                            value: contactMethods,
                                        },
                                    ],
                                },
                            ] })),
                        closeLabel: t('common.cancel'),
                    });
                } }, t('pages.edit.recipients.viewMissingRecipientDetails')) }, t('pages.edit.recipients.recipientsMissingContactsDetailsWarning', {
            count: accountWithoutContactDetailsTotal + externalRecipientWithoutContactDetailsTotal,
        }))) : null));
};
